import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  ExternalLink,
  Loader,
  Maximize2,
  Minimize2,
  RotateCw,
  Star,
  Ticket,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { useTicketTranscript } from '../hooks/useTicketTranscript';
import { NotesApiError } from '../services/notesApi';
import { getTicketsxTranscriptUrl } from '../utils/ticketsx';
import { useIsMobile } from '../hooks/useIsMobile';
import { cn } from '../utils/cn';

interface TicketTranscriptModalProps {
  ticketId: number;
  onClose: () => void;
}

const MIN_WIDTH = 640;
const MIN_HEIGHT = 420;
const VIEWPORT_MARGIN = 32;

function initialFrame() {
  const w = Math.max(320, Math.min(1100, window.innerWidth - VIEWPORT_MARGIN * 2));
  const h = Math.max(320, Math.min(820, window.innerHeight - VIEWPORT_MARGIN * 2));
  return {
    size: { w, h },
    pos: {
      x: Math.max(0, Math.round((window.innerWidth - w) / 2)),
      y: Math.max(0, Math.round((window.innerHeight - h) / 2)),
    },
  };
}

interface ErrorPresentation {
  title: string;
  message: string;
  tone: 'red' | 'amber';
  canRetry: boolean;
  externalUrl: string | null;
}

function describeError(error: Error, ticketId: number): ErrorPresentation {
  const ticketsxUrl = getTicketsxTranscriptUrl(ticketId);
  if (error instanceof NotesApiError) {
    switch (error.status) {
      case 403:
        return {
          title: 'No preview access',
          message: 'You need admin access to preview transcripts in-app. You may still be able to view the ticket on TicketsX.',
          tone: 'red',
          canRetry: false,
          externalUrl: ticketsxUrl,
        };
      case 404:
        return {
          title: 'Transcript not found',
          message: `No transcript exists for ticket #${ticketId} — the ticket may still be open.`,
          tone: 'red',
          canRetry: false,
          externalUrl: ticketsxUrl,
        };
      case 413:
        return {
          title: 'Transcript too large',
          message: 'This transcript is too large to preview in-app. Open it on TicketsX instead.',
          tone: 'red',
          canRetry: false,
          externalUrl: error.transcriptUrl ?? ticketsxUrl,
        };
      case 429:
        return {
          title: 'Rate limited',
          message: `The transcript service is rate limited. Try again in ${
            error.retryAfterSeconds ? `${error.retryAfterSeconds}s` : 'about a minute'
          }.`,
          tone: 'amber',
          canRetry: true,
          externalUrl: null,
        };
      case 502:
        return {
          title: 'TicketsX unreachable',
          message: 'The transcript service is unreachable right now. Try again shortly.',
          tone: 'red',
          canRetry: true,
          externalUrl: ticketsxUrl,
        };
      case 503:
        return {
          title: 'Preview not configured',
          message: "Transcript preview isn't configured on the server yet.",
          tone: 'red',
          canRetry: false,
          externalUrl: ticketsxUrl,
        };
    }
  }
  return {
    title: 'Failed to load transcript',
    message: error.message || 'Something went wrong while loading the transcript.',
    tone: 'red',
    canRetry: true,
    externalUrl: ticketsxUrl,
  };
}

const formatUnix = (seconds: number) => format(new Date(seconds * 1000), 'd MMM yyyy, HH:mm');

export const TicketTranscriptModal = ({ ticketId, onClose }: TicketTranscriptModalProps) => {
  const { data, error, loading, retry } = useTicketTranscript(ticketId);
  const isMobile = useIsMobile();
  const [maximized, setMaximized] = useState(false);
  const [{ pos, size }, setFrame] = useState(initialFrame);
  // While a drag/resize is active the iframe must not swallow pointer events.
  const [interacting, setInteracting] = useState(false);
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);
  const resizeRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);

  // Capture-phase Escape so parent overlays with bubble-phase window listeners
  // (e.g. the kanban card drawer) don't also close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const draggable = !isMobile && !maximized;

  const onTitlePointerDown = (e: React.PointerEvent) => {
    if (!draggable) return;
    if ((e.target as HTMLElement).closest('button, a, input, select')) return;
    dragRef.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    setInteracting(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onTitlePointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const { dx, dy } = dragRef.current;
    setFrame(f => ({
      ...f,
      pos: {
        // Keep enough of the title bar on-screen to grab it back.
        x: Math.min(Math.max(0, e.clientX - dx), Math.max(0, window.innerWidth - 160)),
        y: Math.min(Math.max(0, e.clientY - dy), Math.max(0, window.innerHeight - 48)),
      },
    }));
  };

  const onTitlePointerUp = () => {
    dragRef.current = null;
    setInteracting(false);
  };

  const onResizePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    resizeRef.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };
    setInteracting(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onResizePointerMove = (e: React.PointerEvent) => {
    if (!resizeRef.current) return;
    const start = resizeRef.current;
    setFrame(f => ({
      ...f,
      size: {
        w: Math.min(Math.max(MIN_WIDTH, start.w + e.clientX - start.x), window.innerWidth),
        h: Math.min(Math.max(MIN_HEIGHT, start.h + e.clientY - start.y), window.innerHeight),
      },
    }));
  };

  const onResizePointerUp = () => {
    resizeRef.current = null;
    setInteracting(false);
  };

  const frameStyle: React.CSSProperties = isMobile
    ? { inset: 8, width: 'auto', height: 'auto' }
    : maximized
      ? { inset: 16, width: 'auto', height: 'auto' }
      : { left: pos.x, top: pos.y, width: size.w, height: size.h };

  const meta = data?.transcript;
  const rating = meta?.Rating ?? 0;
  const subtitleParts: string[] = [];
  if (meta?.PanelName) subtitleParts.push(meta.PanelName);
  if (meta?.AuthorUsername) subtitleParts.push(`by ${meta.AuthorUsername}`);
  if (meta?.CreatedAt) {
    subtitleParts.push(
      meta.ClosedAt ? `${formatUnix(meta.CreatedAt)} → ${formatUnix(meta.ClosedAt)}` : formatUnix(meta.CreatedAt),
    );
  }
  if (meta?.CloseReason) subtitleParts.push(meta.CloseReason);
  const subtitle = subtitleParts.join(' · ');

  const errorInfo = error ? describeError(error, ticketId) : null;

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[99999]"
      style={{ zIndex: 99999 }}
      data-outside-click-ignore
      role="dialog"
      aria-modal="true"
      aria-label={`Ticket #${ticketId} transcript`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      // Don't let clicks inside the portal reach the row/panel that rendered the link.
      onClick={e => e.stopPropagation()}
    >
      <div className="absolute inset-0 bg-[var(--bg-overlay)] backdrop-blur-[6px]" onClick={onClose} />
      {/* Geometry lives on a plain div so drag/resize track the pointer 1:1
          (framer-motion would spring-animate style changes); the transition
          class smooths maximize/restore only while no gesture is active. */}
      <div className={cn('absolute', !interacting && 'transition-all duration-200')} style={frameStyle}>
        <motion.div
          className="w-full h-full glass-card overflow-hidden flex flex-col shadow-popover"
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', duration: 0.3 }}
        >
          <div
            className={cn(
              'flex items-center gap-3 px-3 md:px-4 py-2 border-b border-glass-200/10 bg-glass-100/5 flex-shrink-0 select-none touch-none',
              draggable && 'cursor-grab active:cursor-grabbing',
            )}
            onPointerDown={onTitlePointerDown}
            onPointerMove={onTitlePointerMove}
            onPointerUp={onTitlePointerUp}
          >
            <span className="icon-tile icon-tile-sm icon-tile-accent"><Ticket className="w-3.5 h-3.5" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-white truncate">Ticket #{ticketId}</p>
                {rating > 0 && (
                  <span className="flex items-center gap-0.5 flex-shrink-0" title={`Rated ${rating}/5`}>
                    {Array.from({ length: 5 }, (_, i) => (
                      <Star
                        key={i}
                        className={cn('w-3 h-3', i < rating ? 'text-amber-400 fill-amber-400' : 'text-glass-500')}
                      />
                    ))}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-glass-500 truncate tabular-nums" title={subtitle || undefined}>
                {loading ? 'Loading transcript…' : subtitle || 'Transcript preview'}
              </p>
            </div>
            <a
              href={getTicketsxTranscriptUrl(ticketId)}
              target="_blank"
              rel="noopener noreferrer"
              className="glass-button-secondary min-h-0 h-7 px-2 text-xs gap-1.5 flex-shrink-0"
              title="Open this ticket on TicketsX"
              aria-label="Open this ticket on TicketsX"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Open in TicketsX</span>
            </a>
            {!isMobile && (
              <button
                type="button"
                onClick={() => setMaximized(v => !v)}
                className="btn-icon btn-icon-sm flex-shrink-0"
                title={maximized ? 'Restore' : 'Maximize'}
                aria-label={maximized ? 'Restore' : 'Maximize'}
              >
                {maximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="btn-icon btn-icon-sm flex-shrink-0"
              title="Close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 min-h-0 relative">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center gap-3">
                <Loader className="w-5 h-5 animate-spin text-accent-400" />
                <span className="text-sm text-glass-300">Loading transcript…</span>
              </div>
            )}

            {errorInfo && (
              <div className="absolute inset-0 flex items-center justify-center p-6">
                <div
                  className={cn(
                    'alert max-w-md w-full',
                    errorInfo.tone === 'amber' ? 'alert-warning' : 'alert-danger',
                  )}
                >
                    <AlertCircle
                      className={cn(
                        errorInfo.tone === 'amber' ? 'text-amber-400' : 'text-red-400',
                      )}
                    />
                    <div className="min-w-0">
                      <p
                        className={cn(
                          'text-sm font-medium',
                          errorInfo.tone === 'amber' ? 'text-amber-300' : 'text-red-300',
                        )}
                      >
                        {errorInfo.title}
                      </p>
                      <p className="text-sm text-glass-300 mt-1">{errorInfo.message}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {errorInfo.canRetry && (
                          <button
                            type="button"
                            onClick={retry}
                            className="glass-button-sm"
                          >
                            <RotateCw className="w-3.5 h-3.5" />
                            Retry
                          </button>
                        )}
                        {errorInfo.externalUrl && (
                          <a
                            href={errorInfo.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="glass-button-secondary min-h-0 h-[1.875rem] px-2.5 text-xs gap-1.5"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            Open in TicketsX
                          </a>
                        )}
                      </div>
                    </div>
                </div>
              </div>
            )}

            {data && (
              // allow-scripts WITHOUT allow-same-origin: transcript scripts run in an
              // opaque origin with no access to CF cookies or the parent DOM. Never
              // add allow-same-origin here — with srcDoc it would inherit our origin.
              <iframe
                srcDoc={data.html}
                sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
                className={cn('w-full h-full border-0 bg-[#36393e]', interacting && 'pointer-events-none')}
                style={{ colorScheme: 'dark' }}
                title={`Ticket #${ticketId} transcript`}
              />
            )}
          </div>

          {draggable && (
            <div
              className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize touch-none"
              onPointerDown={onResizePointerDown}
              onPointerMove={onResizePointerMove}
              onPointerUp={onResizePointerUp}
            />
          )}
        </motion.div>
      </div>
    </motion.div>,
    document.body,
  );
};
